/**
 * Protocol central <-> web client, copied from docs/DESIGN.md §6 (REST §6.1, WebSocket /ws/client §6.2).
 *
 * Wire format of /ws/client: FLAT JSON objects `{ "type": "...", ...fields }` (same shape as the
 * `spatial` example in §6.2; matches central/src/protocol/client.ts).
 *
 * Runtime validation of incoming frames lives in ./validate.ts.
 */

// ------------------------------------------------------------------ common

/** Minecraft UUID with dashes; also the LiveKit participant identity (§7.3). */
export type Uuid = string;

export type ScopeKind = 'server' | 'match';

export interface Scope {
  kind: ScopeKind;
  /** Human readable label (e.g. `lobby`, `bedwars @ bw1`). */
  label: string;
}

export type AudioMode = 'spatial' | 'global';

/** Role inside a match (§5.5 `match.assign`). */
export type MatchRole = 'PLAYER' | 'SPECTATOR';

export interface GainConfig {
  refDistance: number;
  hearRadius: number;
  rolloffExponent: number;
}

// ------------------------------------------------------------------ REST §6.1

export interface LinkCodeRequest {
  /** 8 Crockford base32 chars (dashes and lowercase tolerated). */
  code: string;
}

/** 200 of POST /api/link (+ Set-Cookie vc_sid). */
export interface LinkResponse {
  uuid: Uuid;
  name: string;
}

/** 200 of GET /api/me. */
export interface MeResponse {
  uuid: Uuid;
  name: string;
  mcOnline: boolean;
  server: string | null;
  /** ms epoch. */
  linkedAt: number;
}

/** GET /api/config. */
export interface ConfigResponse {
  livekitUrl: string;
  spatialDefaults: GainConfig & {
    spatialHz: number;
    interpDelayMs: number;
    usePitch?: boolean;
  };
  version: string;
}

/** Error body of the REST API, e.g. `{ "error": "INVALID_OR_EXPIRED" }`. */
export interface ErrorResponse {
  error: string;
  message?: string;
}

export type LinkErrorCode = 'INVALID_OR_EXPIRED' | 'RATE_LIMITED' | 'BAD_REQUEST' | string;

// ------------------------------------------------------------------ WS server -> client §6.2

export interface HelloMessage {
  type: 'hello';
  uuid: Uuid;
  name: string;
  serverTime: number;
  config: {
    spatialHz: number;
    interpDelayMs: number;
    gain: GainConfig;
  };
}

export interface StateMessage {
  type: 'state';
  mc: 'online' | 'offline';
  server: string | null;
  scope: Scope | null;
  role?: MatchRole;
  teamId?: string | null;
}

export interface RoomAssignMessage {
  type: 'room.assign';
  epoch: number;
  room: string;
  livekitUrl: string;
  token: string;
  /** ms epoch. */
  expiresAt: number;
  scope: Scope;
  audio: { mode: AudioMode };
}

export type RoomLeaveReason = 'MC_OFFLINE' | 'REVOKED' | 'SERVER_DOWN' | 'NO_SCOPE';

export interface RoomLeaveMessage {
  type: 'room.leave';
  epoch: number;
  reason: RoomLeaveReason;
}

export interface TokenIssuedMessage {
  type: 'token.issued';
  epoch: number;
  token: string;
  expiresAt: number;
}

/**
 * Network path of a browser's WebRTC connection to LiveKit (selected ICE candidate pair, local side):
 * - `direct`: UDP straight to the SFU (local candidate `host`/`srflx`/`prflx`, protocol `udp`);
 * - `relay`: TURN relay (local candidate `relay`) or ICE/TCP (fallback when UDP is blocked).
 */
export type NetKind = 'direct' | 'relay';
/** Detail: `udp` (direct), `tcp` (ICE/TCP), `turn-udp` | `turn-tcp` | `turn-tls` (TURN by relayProtocol). */
export type NetVia = 'udp' | 'tcp' | 'turn-udp' | 'turn-tcp' | 'turn-tls';
export const NET_KINDS: readonly NetKind[] = ['direct', 'relay'];
export const NET_VIAS: readonly NetVia[] = ['udp', 'tcp', 'turn-udp', 'turn-tcp', 'turn-tls'];

export interface PeerInfo {
  identity: Uuid;
  name: string;
  teamId?: string | null;
  role: MatchRole;
  radio: boolean;
  /** Network path of that player (omitted while unknown: bots, old browsers, not measured yet). */
  net?: NetKind;
  via?: NetVia;
}

export interface PeersMessage {
  type: 'peers';
  epoch: number;
  peers: PeerInfo[];
}

/**
 * Publisher-side subscription permissions (protocol/README.md; not in DESIGN §6,
 * added by the central). Applied with
 * `room.localParticipant.setTrackSubscriptionPermissions(false, allow.map(id => ({ participantIdentity: id, allowAll: true })))`.
 * Sent when connecting to a room and whenever the set of authorized listeners changes.
 */
export interface PermissionsMessage {
  type: 'permissions';
  epoch: number;
  /** Identities allowed to subscribe to my microphone. */
  allow: Uuid[];
}

/** Spatial flags bitmask (§6.2). */
export const SPATIAL_FLAG_RADIO = 1;
export const SPATIAL_FLAG_TELEPORT = 2;
export const SPATIAL_FLAG_POLICY_MUTED = 4;

/** `[identity, dx, dy, dz, flags]`: offset speaker eyes - listener eyes, Minecraft axes. */
export type SpatialTuple = [identity: Uuid, dx: number, dy: number, dz: number, flags: number];

export interface SpatialMessage {
  type: 'spatial';
  epoch: number;
  /** Server time, ms epoch. */
  t: number;
  self: { yaw: number; pitch: number };
  p: SpatialTuple[];
}

export type KickReason = 'OTHER_TAB' | 'UNLINKED' | 'SESSION_EXPIRED';

export interface KickMessage {
  type: 'kick';
  reason: KickReason;
}

export interface ErrorMessage {
  type: 'error';
  code: string;
  message: string;
}

export interface PongMessage {
  type: 'pong';
  n: number;
}

export type ServerToClient =
  | HelloMessage
  | StateMessage
  | RoomAssignMessage
  | RoomLeaveMessage
  | TokenIssuedMessage
  | PeersMessage
  | PermissionsMessage
  | SpatialMessage
  | KickMessage
  | ErrorMessage
  | PongMessage;

export type ServerMessageType = ServerToClient['type'];

export const SERVER_MESSAGE_TYPES: readonly ServerMessageType[] = [
  'hello',
  'state',
  'room.assign',
  'room.leave',
  'token.issued',
  'peers',
  'permissions',
  'spatial',
  'kick',
  'error',
  'pong',
] as const;

// ------------------------------------------------------------------ WS client -> server §6.2

export interface RoomJoinedMessage {
  type: 'room.joined';
  epoch: number;
  room: string;
}

export interface RoomFailedMessage {
  type: 'room.failed';
  epoch: number;
  room: string;
  error: string;
}

export interface TokenRequestMessage {
  type: 'token.request';
  epoch: number;
}

export type MicMode = 'vad' | 'ptt';

export interface PrefsMessage {
  type: 'prefs';
  micMode: MicMode;
}

export interface PingMessage {
  type: 'ping';
  n: number;
}

/**
 * `net {epoch, net, via}`: network path of my LiveKit connection for the room of `epoch`.
 * Sent only when it changes and once after every new epoch (`direct` always goes with `udp`).
 */
export interface NetMessage {
  type: 'net';
  epoch: number;
  net: NetKind;
  via: NetVia;
}

/**
 * `mic {muted, deafened}`: stato del microfono scelto dal giocatore, mostrato in gioco (placeholder `%voicechat_muted%`,
 * `%voicechat_status%`, `%voicechat_deafened%`).
 * - `muted`: non trasmette per scelta o perché non può: pulsante Muto, mute automatico per fischio (finché non lo
 *   riattiva), microfono negato/assente/occupato/in errore. Il push-to-talk col tasto rilasciato NON è muto.
 * - `deafened`: ascolto disattivato (pulsante "Audio").
 * Inviato dopo ogni `hello` (connessione o riconnessione del WS) e poi solo quando cambia, al massimo uno ogni
 * {@link MIC_MIN_INTERVAL_MS} ms. Non ha epoch: vale per la connessione WS, il centrale lo azzera alla disconnessione.
 */
export interface MicMessage {
  type: 'mic';
  muted: boolean;
  deafened: boolean;
}

/** Intervallo minimo tra due `mic` (toggle rapidi o tasto tenuto premuto: resta ben sotto il rate limit del centrale). */
export const MIC_MIN_INTERVAL_MS = 250;

export type ClientToServer =
  | RoomJoinedMessage
  | RoomFailedMessage
  | TokenRequestMessage
  | PrefsMessage
  | PingMessage
  | NetMessage
  | MicMessage;

export type ClientMessageType = ClientToServer['type'];

export const CLIENT_MESSAGE_TYPES: readonly ClientMessageType[] = [
  'room.joined',
  'room.failed',
  'token.request',
  'prefs',
  'ping',
  'net',
  'mic',
] as const;

/** Close codes used by the central on /ws/client. */
export const CLIENT_CLOSE = {
  BAD_MESSAGE: 4400,
  UNAUTHORIZED: 4401,
  KICKED: 4409,
  RATE_LIMITED: 4429,
} as const;

/** Defaults of §3.3, used until `hello` arrives. */
export const DEFAULT_GAIN: GainConfig = { refDistance: 4, hearRadius: 32, rolloffExponent: 1.5 };
export const DEFAULT_INTERP_DELAY_MS = 100;
export const DEFAULT_SPATIAL_HZ = 10;
/** Client ping period (§6.2). */
export const PING_INTERVAL_MS = 15_000;
